import React from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { RECOVERY_FACTS, SUPPORT_MESSAGE } from '@/components/planning/recoveryGuideContent';

const Step = ({ n, title, children }) => (
  <section className="rounded-lg border bg-white p-4" data-testid={`guide-step-${n}`}>
    <h2 className="mb-2 font-semibold text-slate-900">Étape {n} — {title}</h2>
    <div className="space-y-1 text-sm text-slate-700">{children}</div>
  </section>
);

export default function RecoveryGuide({ user }) {
  const copy = async () => {
    try { await navigator.clipboard.writeText(SUPPORT_MESSAGE); toast.success('Message copié'); } catch (e) { toast.error('Copie impossible : sélectionnez le texte manuellement'); }
  };
  if (!user?.is_super_admin) {
    return <Layout user={user} activeModule="b2b"><p className="p-6" data-testid="guide-forbidden">Réservé au Super Admin.</p></Layout>;
  }
  return (
    <Layout user={user} activeModule="b2b">
      <div className="mx-auto max-w-4xl space-y-4 p-4" data-testid="recovery-guide-page">
        <header>
          <h1 className="text-2xl font-bold text-slate-900">Guide de récupération des journées supprimées</h1>
          <p className="text-sm text-slate-600">Aucune restauration n’est lancée depuis cette page. Toute restauration en production nécessite votre validation écrite.</p>
        </header>
        <section className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="guide-facts">
          <h2 className="mb-2 font-semibold">Constat en production (lecture seule)</h2>
          <ul className="list-disc space-y-1 pl-5">{RECOVERY_FACTS.map((f) => <li key={f}>{f}</li>)}</ul>
        </section>
        <Step n={1} title="Vérifier les sauvegardes disponibles">
          <p>Envoyer au support Emergent le message ci-dessous (bouton d’aide de la plateforme). Il demande la liste des sauvegardes couvrant février à juin 2026 et leur conservation pendant l’analyse.</p>
          <pre className="whitespace-pre-wrap rounded border bg-slate-50 p-3 text-xs" data-testid="guide-support-message">{SUPPORT_MESSAGE}</pre>
          <Button size="sm" onClick={copy} data-testid="guide-copy-support">Copier le message</Button>
        </Step>
        <Step n={2} title="Si une sauvegarde existe : restauration dans une copie séparée">
          <p>1. La sauvegarde est restaurée dans une base séparée, jamais sur la base en ligne.</p>
          <p>2. Seules les journées des sites Colis Privé antérieures au 01/07/2026 sont extraites de la copie.</p>
          <p>3. Comparaison salarié + date avec la base en ligne : une journée déjà présente est ignorée (ni écrasement, ni doublon).</p>
          <p>4. La liste des journées absentes vous est présentée pour validation, avec leur origine.</p>
          <p>5. Après votre accord écrit uniquement, insertion des seules journées absentes, marquées « restaurée depuis sauvegarde du … ». Contrôle ensuite des doublons (0 attendu) et du Calcul salaire des mois concernés.</p>
        </Step>
        <Step n={3} title="Sans sauvegarde : examen des preuves, sans reconstitution automatique">
          <p>La liste « Journées attestées par photo » (page Intégrité du planning) présente chaque journée avec ses preuves et les informations manquantes. Elle est exportable en CSV.</p>
          <p>Une photo prouve une présence ; elle ne donne ni les heures, ni la validation. Aucune journée n’est recréée et rien n’entre dans la paie sans décision explicite, journée par journée.</p>
          <a href="/admin/planning/integrity" className="font-medium text-blue-700 underline" data-testid="guide-integrity-link">Ouvrir la liste à examiner</a>
        </Step>
      </div>
    </Layout>
  );
}
